import * as React from 'react';
import { inject, observer } from 'mobx-react';
import { withTranslation, WithTranslation } from 'react-i18next';
import cloneDeep from 'lodash/cloneDeep';
import set from 'lodash/set';
import { logger } from '@lib/common';
import { Button, DotsLoader, RotateLoader, keyframes, styled, Textarea } from '@lib/components';
import { FaChevronRight, FaHistory, FaMagic, FaPaperPlane, FaPlus, FaUser } from 'react-icons/fa';
import { IoMdClose } from 'react-icons/io';
import { MobxComponent } from '../../../../../../../mobx/components';
import { UI } from '../../../../../../../core/ui';
import {
	Conversation,
	Message,
	load as loadConversations,
	newConversation,
	save as saveConversations,
	title as conversationTitle,
} from './conversations';

/**
 * "Ask AI" chat for the whole website.
 *
 * One conversation covering the whole site rather than one per section. An
 * operator asking for "a warmer looking menu" does not think in terms of which
 * accordion owns which field: mapping a request onto settings is the assistant's
 * problem to solve, not theirs.
 *
 * Deliberately does not show the operator any generated code. Asking someone who
 * cannot read CSS to approve a diff produces a rubber stamp, not a review, so
 * what they get instead is a plain-language summary and the change itself in the
 * Design Mode preview. The generated code is still reachable in the existing
 * Custom CSS & Javascript section, for anyone technical who goes looking.
 *
 * Changes are written as soon as they arrive rather than held for confirmation,
 * with Undo on the most recent one. That trades a confirmation step on a live
 * ordering site for a shorter loop, and it means Undo is the only safety net:
 * once Design Mode is left or the page reloaded, the revert patch is gone.
 *
 * A column of the Design Mode layout rather than a section of the settings
 * accordion: SettingsSectionBlock unmounts its children when collapsed, which
 * would throw away the conversation every time another section was opened.
 */

/**
 * A column in the Design Mode layout rather than a panel over it. The shared
 * Sidebar is modal: a fixed backdrop and a scroll lock on the page behind. Here
 * that would mean choosing between the chat and the settings and preview the
 * chat exists to change, so the preview gives up width instead.
 */
const Panel = styled('div')`
	display: flex;
	flex-direction: column;
	flex: 0 0 380px;
	height: 100%;
	background: white;
	border-left: 1px solid #e0e0e0;
	box-shadow: -4px 0 16px rgba(0, 0, 0, 0.08);
`;

const Header = styled('div')`
	display: flex;
	align-items: center;
	justify-content: space-between;
	flex: 0 0 auto;
	padding: 16px;
	border-bottom: 1px solid #dfdfdf;
	font-size: 15px;
	font-weight: 600;
	color: #0c202a;
	svg {
		cursor: pointer;
		color: #4b4f5e;
	}
`;

const Thread = styled('div')`
	flex: 1;
	/* Flex items will not shrink below their content by default, which in a
	   column means the thread grows and the composer is pushed off instead of
	   the thread scrolling. */
	min-height: 0;
	overflow-y: auto;
	padding: 16px;
	::-webkit-scrollbar {
		width: 5px;
	}
	::-webkit-scrollbar-thumb {
		background-color: #d9d9d9;
	}
`;

/**
 * Entry animation. Short and small on purpose: a reply can take fifteen seconds,
 * so the arrival wants marking, but this fires on every turn and anything longer
 * would be in the way by the third one.
 */
const appear = keyframes`
	from { opacity: 0; transform: translateY(4px); }
	to { opacity: 1; transform: none; }
`;

/** One turn: avatar beside the bubble, reversed so the operator's sit right. */
const Row = styled('div')<{ own?: boolean }>`
	display: flex;
	align-items: flex-start;
	gap: 8px;
	margin-bottom: 14px;
	flex-direction: ${({ own }) => (own ? 'row-reverse' : 'row')};
	animation: ${appear} 0.18s ease-out;
`;

const Avatar = styled('div')<{ own?: boolean }>`
	flex: 0 0 26px;
	width: 26px;
	height: 26px;
	margin-top: 2px;
	border-radius: 50%;
	display: flex;
	align-items: center;
	justify-content: center;
	font-size: 11px;
	color: ${({ own }) => (own ? '#5a6270' : 'white')};
	background: ${({ own }) => (own ? '#e3e8ef' : '#2f6fd0')};
`;

/**
 * The bubble, any proposal card, and the timestamp for one turn. Bounded so a
 * long reply leaves the avatar column alone instead of overrunning it.
 */
const Stack = styled('div')<{ own?: boolean }>`
	display: flex;
	flex-direction: column;
	align-items: ${({ own }) => (own ? 'flex-end' : 'flex-start')};
	min-width: 0;
	max-width: calc(100% - 34px);
`;

const Bubble = styled('div')<{ own?: boolean }>`
	padding: 10px 12px;
	border-radius: 8px;
	font-size: 14px;
	line-height: 1.5;
	white-space: pre-wrap;
	/* A pasted URL is a single unbreakable token, and pre-wrap alone will not
	   break it. Without this it runs past the edge of the sidebar. */
	overflow-wrap: break-word;
	background: ${({ own }) => (own ? '#e9f2ff' : '#f4f5f7')};
`;

const Time = styled('span')`
	margin-top: 3px;
	padding: 0 2px;
	font-size: 11px;
	color: #9aa0ae;
`;

const Typing = styled('div')`
	display: flex;
	align-items: center;
	gap: 8px;
	font-size: 14px;
	color: #6b7280;
`;

const Proposal = styled('div')`
	width: 100%;
	padding: 12px;
	border: 1px solid #d9e5f5;
	border-radius: 8px;
	background: #fbfdff;
	font-size: 14px;
	overflow-wrap: break-word;
	/* Only spaced when it follows the prose it belongs to. */
	&:not(:first-child) {
		margin-top: 6px;
	}
`;

const Summary = styled('p')`
	font-size: 14px;
	font-weight: 600;
	line-height: 1.45;
`;

const Changes = styled('div')`
	display: flex;
	flex-direction: column;
	gap: 6px;
	margin-top: 10px;
`;

/**
 * Each change stacked as label above value, rather than a row with the two
 * pushed to opposite edges. In a 380px column that gap grew wide enough that
 * reading across it took effort, and a long value had nowhere to wrap.
 */
const Change = styled('div')`
	padding: 8px 10px;
	border-radius: 6px;
	background: #f2f7fd;
`;

const ChangeMeta = styled('span')`
	display: block;
	font-size: 11px;
	line-height: 1.3;
	color: #7c8496;
`;

const ChangeValue = styled('span')`
	display: block;
	margin-top: 2px;
	font-size: 14px;
	font-weight: 600;
	line-height: 1.35;
	overflow-wrap: break-word;
`;

const Note = styled('p')`
	margin-top: 8px;
	font-size: 13px;
	color: #7a7f8c;
`;

const Status = styled('div')`
	display: flex;
	align-items: center;
	gap: 12px;
	margin-top: 10px;
	padding-top: 10px;
	/* Separates what changed from what became of it. */
	border-top: 1px solid #e8eef7;
	font-size: 13px;
`;

const UndoLink = styled('button')`
	border: none;
	background: none;
	padding: 0;
	font-size: 13px;
	font-weight: 600;
	color: #2f6fd0;
	text-decoration: underline;
	cursor: pointer;
	&:disabled {
		color: #9aa0ae;
		cursor: default;
		text-decoration: none;
	}
`;

const Swatch = styled('span')<{ color: string }>`
	display: inline-block;
	width: 14px;
	height: 14px;
	border-radius: 3px;
	border: 1px solid rgba(0, 0, 0, 0.15);
	background: ${({ color }) => color};
	vertical-align: middle;
	margin-right: 4px;
`;

const HistoryBar = styled('div')`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 8px;
	padding: 8px 12px;
	border-bottom: 1px solid #e6e6e6;
	background: #fbfbfc;
`;

const HistoryToggle = styled('button')`
	display: inline-flex;
	align-items: center;
	border: none;
	background: none;
	padding: 4px 6px;
	border-radius: 4px;
	font-size: 13px;
	font-weight: 600;
	color: #2f6fd0;
	cursor: pointer;
	&:disabled {
		color: #9aa0ae;
		cursor: default;
	}
	&:hover:not(:disabled) {
		background: #eaf2fe;
	}
`;

const HistoryList = styled('div')`
	max-height: 180px;
	overflow-y: auto;
	border-bottom: 1px solid #e6e6e6;
	background: white;
`;

const HistoryItem = styled('div')<{ active?: boolean }>`
	padding: 8px 12px;
	font-size: 13px;
	line-height: 1.4;
	overflow-wrap: break-word;
	cursor: pointer;
	border-bottom: 1px solid #f2f4f7;
	background: ${({ active }) => (active ? '#eaf2fe' : 'white')};
	&:hover {
		background: #f4f6f9;
	}
`;

const HistoryTime = styled('span')`
	display: block;
	margin-top: 2px;
	font-size: 11px;
	color: #8b90a0;
`;

const Composer = styled('div')`
	padding: 12px 16px 16px 16px;
	border-top: 1px solid #dfdfdf;
	background: #fefefe;
	/* Matched to the bubbles, so what is typed is not smaller than what it
	   becomes once sent. */
	textarea {
		font-size: 14px;
	}
`;

const Hint = styled('p')`
	font-size: 12px;
	color: #7a7f8c;
	margin-top: 8px;
`;

/**
 * The settings block owning inject.html_head / html_body. Generated code lands
 * there whatever the request was about, so that form goes stale too and has to be
 * rebuilt alongside the forms for any settings that changed.
 */
export const AI_INJECT_SECTION = 'Custom CSS & Javascript';

interface Props extends WithTranslation {
	open: boolean;
	onClose: () => void;
	/** Renders CSS in the preview iframe. Empty string clears it. */
	onPreviewCss: (css: string) => void;
	/**
	 * Fired after a change is written or undone, naming every settings block it
	 * touched, so those forms are rebuilt from the updated restaurant instead of
	 * holding the values they mounted with.
	 */
	onApplied: (sections: string[]) => void;
}

interface State {
	conversations: Conversation[];
	active_id: string;
	draft: string;
	loading: boolean;
	listing: boolean;
	usage: T.API.AIUsageSummary | null;
}

const isColour = (value: any): boolean =>
	typeof value === 'string' && /^(#|rgba?\()/.test(value.trim());

/**
 * Strips the markdown the model still reaches for occasionally, despite being
 * told not to. Stripped rather than rendered: turning model output into markup
 * would need a sanitiser, which is a lot of new surface for some bold text.
 *
 * Single underscores are left alone deliberately — a setting path like
 * website.sections.top_nav.text.logo would be mangled by an italics rule.
 */
const plainText = (text: string): string =>
	text
		.replace(/\*\*([\s\S]+?)\*\*/g, '$1')
		.replace(/`([^`\n]+)`/g, '$1')
		.replace(/^#{1,6}\s+/gm, '')
		.replace(/^\s*[-*]\s+/gm, '• ');

const readPath = (source: any, path: string): any =>
	path.split('.').reduce((node: any, key: string) => (node == null ? undefined : node[key]), source);

const describe = (value: any): string => {
	if (value === true) return 'on';
	if (value === false) return 'off';
	if (value === undefined || value === null || value === '') return 'not set';
	// A font is an object of family, category and query; only the family means
	// anything to the operator.
	if (typeof value === 'object' && typeof value.family === 'string') return value.family;
	// An image is identified by a CDN id the operator has never seen, so fall back
	// to the file name they uploaded or the stock name they were offered.
	if (typeof value === 'object' && typeof value._id === 'string') return value.name || 'an image';
	return String(value);
};

@inject('store')
@observer
class AskAIPanelClass extends MobxComponent<Props, State> {
	threadRef: HTMLDivElement | null = null;

	constructor(props: Props) {
		super(props);
		const blank = newConversation();
		this.state = {
			conversations: [blank],
			active_id: blank.id,
			draft: '',
			loading: false,
			listing: false,
			usage: null,
		};
	}

	componentDidMount() {
		const stored = loadConversations(this.restaurantId);
		if (!stored.length) return;
		// Land on a fresh chat rather than resuming. Past conversations are a record
		// of changes already made, not work in progress.
		const blank = newConversation();
		this.setState({
			conversations: [blank, ...stored.sort((a, b) => b.updated - a.updated)],
			active_id: blank.id,
		});
	}

	componentDidUpdate() {
		this.scrollToEnd();
	}

	scrollToEnd = () => {
		if (this.threadRef) {
			this.threadRef.scrollTop = this.threadRef.scrollHeight;
		}
	};

	get restaurantId(): string {
		return this.injected.store.restaurant!._id;
	}

	get messages(): Message[] {
		const active = this.state.conversations.find(c => c.id === this.state.active_id);
		return active ? active.messages : [];
	}

	setMessages = (messages: Message[]) => {
		const conversations = this.state.conversations.map(c =>
			c.id === this.state.active_id ? { ...c, messages, updated: Date.now() } : c
		);
		this.setState({ conversations });
		saveConversations(this.restaurantId, conversations);
	};

	startConversation = () => {
		const blank = newConversation();
		this.props.onPreviewCss('');
		this.setState({
			conversations: [blank, ...this.state.conversations.filter(c => c.messages.length)],
			active_id: blank.id,
			draft: '',
			listing: false,
		});
	};

	openConversation = (id: string) => {
		this.props.onPreviewCss('');
		this.setState({ active_id: id, draft: '', listing: false });
	};

	setDraft = (draft: string) => {
		this.setState({ draft });
	};

	send = async () => {
		const draft = this.state.draft.trim();
		if (!draft || this.state.loading) return;

		const r = this.injected.store.restaurant!;
		// A reply that made a change carries no prose, only a summary. Sending the
		// summary as its content is what stops the turn being dropped as empty:
		// without it the model sees consecutive user messages, and answers the
		// earlier one it thinks is still outstanding instead of the latest.
		const history = this.messages.map(m => ({ role: m.role, content: m.content || m.summary || '' }));
		const messages: Message[] = [
			...this.messages,
			{ role: 'user', content: draft, created: Date.now() },
		];

		this.setMessages(messages);
		this.setDraft('');
		this.setState({ loading: true });

		try {
			const result = await this.injected.store.api.ai_website_chat({
				restaurant_id: r._id,
				message: draft,
				history,
				// Sent so a proposal is composed on top of unsaved edits to the
				// injection fields rather than silently discarding them.
				inject: {
					html_head: (r.website.inject && r.website.inject.html_head) || '',
					html_body: (r.website.inject && r.website.inject.html_body) || '',
				},
			});

			if (result.usage) {
				this.setState({ usage: result.usage });
			}

			if (result.outcome) {
				this.setMessages([
					...messages,
					{
						role: 'assistant',
						content: result.message || 'Something went wrong.',
						created: Date.now(),
					},
				]);
				return;
			}

			const reply: Message = {
				role: 'assistant',
				content: result.reply || '',
				created: Date.now(),
				summary: result.summary,
				proposal: result.proposal,
			};
			const next = [...messages, reply];
			this.setMessages(next);

			if (!result.proposal) return;

			// Shown while the write is in flight, so the change is visible before the
			// iframe has finished reloading off the saved state.
			if (result.proposal.kind === 'code' && result.proposal.preview_css) {
				this.props.onPreviewCss(result.proposal.preview_css);
			}
			await this.applyProposal(reply, next.length - 1);
		} catch (e) {
			logger.captureException(e);
			this.setMessages([
				...messages,
				{
					role: 'assistant',
					content: 'I could not reach the assistant. Please try again.',
					created: Date.now(),
				},
			]);
		} finally {
			this.setState({ loading: false });
		}
	};

	/** Writes a path-to-value patch and mirrors it into the store. */
	write = async (update: T.ObjectAny): Promise<boolean> => {
		const store = this.injected.store;
		const r = cloneDeep(store.restaurant!);
		Object.keys(update).forEach(path => set(r as any, path, update[path]));

		const result = await store.api.restaurant_update({ _id: r._id, update: { $set: update } });
		if (result.outcome) {
			UI.notification.error(result.message || 'Could not save the change');
			return false;
		}
		store.setRestaurant(r);
		return true;
	};

	/**
	 * Applies a proposal to the live site as soon as it arrives, and records the
	 * inverse patch so it can be taken back.
	 *
	 * Takes the message rather than reading it back out of state, because this
	 * runs immediately after the setMessages that added it.
	 */
	applyProposal = async (message: Message, index: number) => {
		const proposal = message.proposal;
		if (!proposal) return;

		const current = cloneDeep(this.injected.store.restaurant!);
		const update: T.ObjectAny = {};
		const revert: T.ObjectAny = {};
		const affected: string[] = [];

		if (proposal.kind === 'settings') {
			proposal.changes.forEach(change => {
				update[change.path] = change.to;
				// The API read `from` off the same document, but reading it here keeps
				// the revert honest if anything moved in between. An absent field has
				// to revert to null rather than undefined, which is dropped when the
				// patch is serialised and would leave Undo silently doing nothing.
				const before = readPath(current, change.path);
				revert[change.path] = before === undefined ? null : before;
				if (affected.indexOf(change.section) === -1) {
					affected.push(change.section);
				}
			});
		} else {
			const inject = current.website.inject || { html_head: '', html_body: '' };
			const customisations = current.website.ai_customisations;

			update['website.inject.html_head'] = proposal.inject.html_head;
			update['website.inject.html_body'] = proposal.inject.html_body;
			// Describes what was changed so it can be summarised, and later removed,
			// without anyone having to read the generated code. Assembled by the API,
			// which owns the region parser this has to stay consistent with.
			update['website.ai_customisations.sections'] = proposal.customisations;

			revert['website.inject.html_head'] = inject.html_head || '';
			revert['website.inject.html_body'] = inject.html_body || '';
			revert['website.ai_customisations.sections'] = (customisations && customisations.sections) || [];

			affected.push(AI_INJECT_SECTION);
		}

		this.setState({ loading: true });
		try {
			if (!(await this.write(update))) {
				this.settle(index, { settled: 'failed' });
				return;
			}
			// The iframe reloads off the saved state now, so the overlay would double
			// up with the real thing.
			this.props.onPreviewCss('');
			this.props.onApplied(affected);
			this.settle(index, { settled: 'applied', revert, affected });
		} catch (e) {
			logger.captureException(e);
			UI.notification.error('Could not save the change');
			this.settle(index, { settled: 'failed' });
		} finally {
			this.setState({ loading: false });
		}
	};

	undo = async (index: number) => {
		const message = this.messages[index];
		if (!message || !message.revert || this.state.loading) return;

		this.setState({ loading: true });
		try {
			if (!(await this.write(message.revert))) return;
			this.props.onPreviewCss('');
			this.props.onApplied(message.affected || []);
			this.settle(index, { settled: 'undone', revert: undefined, affected: undefined });
		} catch (e) {
			logger.captureException(e);
			UI.notification.error('Could not undo the change');
		} finally {
			this.setState({ loading: false });
		}
	};

	/**
	 * Functional update: this runs straight after the setMessages that added the
	 * message, so the list must come from state as React has it, not as this
	 * component last read it.
	 */
	settle = (index: number, patch: Partial<Message>) => {
		this.setState(
			state => ({
				conversations: state.conversations.map(c =>
					c.id === state.active_id
						? {
								...c,
								messages: c.messages.map((m, i) => (i === index ? { ...m, ...patch } : m)),
								updated: Date.now(),
						  }
						: c
				),
			}),
			() => saveConversations(this.restaurantId, this.state.conversations)
		);
	};

	/**
	 * Only the newest applied change offers Undo. Reverting an older one would
	 * write back values that later changes have already replaced.
	 */
	get undoableIndex(): number {
		for (let i = this.messages.length - 1; i >= 0; i--) {
			const m = this.messages[i];
			if (m.settled === 'applied' && m.revert) return i;
		}
		return -1;
	}

	renderProposal = (message: Message, index: number) => {
		const proposal = message.proposal;

		// Restored from a previous session. The summary still reads as a record of
		// what happened, but the revert patch was not persisted, so there is
		// nothing left to undo.
		if (!proposal) {
			return (
				<Proposal>
					<Summary>{plainText(message.summary || 'Change')}</Summary>
					{this.renderStatus(message, index)}
				</Proposal>
			);
		}

		return (
			<Proposal>
				<Summary>{plainText(message.summary || 'Change')}</Summary>

				{proposal.kind === 'settings' && (
					<Changes>
						{proposal.changes.map(change => (
							<Change key={change.path}>
								<ChangeMeta>
									{change.section} · {change.label}
								</ChangeMeta>
								<ChangeValue>
									{isColour(change.to) && <Swatch color={String(change.to)} />}
									{describe(change.to)}
								</ChangeValue>
							</Change>
						))}
					</Changes>
				)}

				{proposal.kind === 'code' && !proposal.preview_css && (
					<Note>This change cannot be shown in the preview.</Note>
				)}

				{this.renderStatus(message, index)}
			</Proposal>
		);
	};

	renderStatus = (message: Message, index: number) => {
		const { settled } = message;
		if (settled === 'failed') {
			return <Status className="error-text">Could not be applied.</Status>;
		}
		if (settled === 'undone') {
			return <Status>Undone.</Status>;
		}
		if (settled !== 'applied') {
			return null;
		}
		return (
			<Status>
				<span className="success-text font-semi-bold">Applied</span>
				{index === this.undoableIndex && (
					<UndoLink disabled={this.state.loading} onClick={() => this.undo(index)}>
						Undo
					</UndoLink>
				)}
			</Status>
		);
	};

	/**
	 * Clock time alone for today, prefixed with the date on anything older.
	 * Conversations are kept across days, so a bare "4:37 pm" on a chat resumed
	 * from last week says nothing.
	 *
	 * The two days are compared as formatted strings rather than by date maths so
	 * the comparison cannot disagree with the rendered value about which timezone
	 * or calendar it is using.
	 */
	messageTime = (created: number): string => {
		const { t } = this.injected;
		const time = t('timeFromTimestamp', { value: created });
		const day = t('dateFromTimestamp', { value: created });
		return day === t('dateFromTimestamp', { value: Date.now() }) ? time : `${day}, ${time}`;
	};

	/**
	 * Relative for recent activity, an absolute date beyond a week. "2 months
	 * ago" is not something anyone can place, and both formats already respect
	 * the operator's locale and timezone.
	 */
	timeLabel = (updated: number): string => {
		if (!updated) return '';
		const { t } = this.injected;
		const week = 7 * 24 * 60 * 60 * 1000;
		return Date.now() - updated < week
			? t('relative', { value: updated })
			: t('dateFromTimestamp', { value: updated });
	};

	renderHistory = () => {
		const { conversations, active_id, listing } = this.state;
		// Sorted here rather than on write, so a chat that was just added to moves
		// to the top and its timestamp agrees with its position.
		const past = conversations.filter(c => c.messages.length).sort((a, b) => b.updated - a.updated);

		return (
			<>
				<HistoryBar>
					<HistoryToggle
						disabled={!past.length}
						onClick={() => past.length && this.setState({ listing: !listing })}
					>
						<FaHistory className="m-r-2" />
						{past.length ? `History (${past.length})` : 'No history yet'}
					</HistoryToggle>
					<HistoryToggle onClick={this.startConversation}>
						<FaPlus className="m-r-2" />
						New chat
					</HistoryToggle>
				</HistoryBar>

				{listing && (
					<HistoryList>
						{past.map(c => (
							<HistoryItem
								key={c.id}
								active={c.id === active_id}
								onClick={() => this.openConversation(c.id)}
							>
								{conversationTitle(c)}
								<HistoryTime>{this.timeLabel(c.updated)}</HistoryTime>
							</HistoryItem>
						))}
					</HistoryList>
				)}
			</>
		);
	};

	render() {
		const { open, onClose } = this.props;
		const { loading, usage, draft } = this.state;

		// Unrendered rather than hidden when closed. The instance stays mounted, so
		// the conversation and its Undo patches survive closing and reopening.
		if (!open) return null;

		return (
			<Panel>
				<Header>
					<span>Ask AI</span>
					<IoMdClose size={22} onClick={onClose} />
				</Header>
				{this.renderHistory()}
				<Thread ref={(el: HTMLDivElement | null) => (this.threadRef = el)}>
					{this.messages.length === 0 && (
						<Row>
							<Avatar>
								<FaMagic />
							</Avatar>
							<Stack>
								<Bubble>
									Describe how you want your website to look and I will change it for you.
									{'\n\n'}
									For example: "make the header title bigger and centred", "use a dark green nav
									bar with cream text", or "hide the item descriptions".
								</Bubble>
							</Stack>
						</Row>
					)}

					{this.messages.map((message, index) => {
						const own = message.role === 'user';
						return (
							<Row key={index} own={own}>
								<Avatar own={own}>{own ? <FaUser /> : <FaMagic />}</Avatar>
								<Stack own={own}>
									{message.content && (
										<Bubble own={own}>
											{own ? message.content : plainText(message.content)}
										</Bubble>
									)}
									{(message.proposal || message.summary) && this.renderProposal(message, index)}
									{!!message.created && <Time>{this.messageTime(message.created)}</Time>}
								</Stack>
							</Row>
						);
					})}

					{loading && (
						<Row>
							<Avatar>
								<FaMagic />
							</Avatar>
							<Stack>
								<Bubble>
									<Typing>
										<span>Thinking</span>
										<DotsLoader size={4} color="#9aa0ae" />
									</Typing>
								</Bubble>
							</Stack>
						</Row>
					)}
				</Thread>

				<Composer>
					<Textarea
						value={draft}
						rows={3}
						maxLength={1000}
						placeholder="Ask for a change to your website"
						disabled={loading}
						onChange={e => this.setDraft(e.target.value)}
						onKeyDown={(e: React.KeyboardEvent) => {
							if (e.key === 'Enter' && !e.shiftKey) {
								e.preventDefault();
								this.send();
							}
						}}
					/>
					<Button
						full={true}
						color="primary"
						className="m-t-2"
						disabled={loading || !draft.trim()}
						onClick={this.send}
					>
						{loading ? <RotateLoader size={2} color="white" /> : <FaPaperPlane className="m-r-2" />}
						{!loading && 'Send'}
					</Button>
					<Hint>
						Changes are applied to your live site straight away. Use Undo to take the last one
						back.
						{usage ? ` ${usage.remaining} credits left until ${usage.resets}.` : ''}
					</Hint>
				</Composer>
			</Panel>
		);
	}
}

export const AskAIPanel = withTranslation()(AskAIPanelClass);

/**
 * Matches the settings accordion header, minus the expand chevron: this row
 * opens the chat panel rather than revealing content below it, and borrowing
 * SettingsSectionBlock would promise an expansion that never happens.
 */
const EntryRow = styled('div')`
	display: flex;
	align-items: center;
	justify-content: space-between;
	width: 100%;
	padding: 16px 20px;
	font-size: 14px;
	cursor: pointer;
	background: white;
	border-bottom: 1px solid #e6e6e6;
	transition: 0.26s all;
	&:hover {
		background: #f2f2f2;
	}
`;

const EntryHint = styled('p')`
	font-size: 12px;
	color: #7a7f8c;
	margin-top: 2px;
`;

export const AskAIEntry = ({ onClick }: { onClick: () => void }) => (
	<EntryRow onClick={onClick}>
		<div className="flex-grow">
			<span className="font-semi-bold">Ask AI</span>
			<EntryHint>Describe a change in your own words</EntryHint>
		</div>
		<FaChevronRight color="#9aa0ae" />
	</EntryRow>
);
